import get from 'lodash.get';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import moment from 'moment-timezone';
import Placeholder from '../../Placeholder';

import withAnalytics, { withAnalyticsPropType } from '../../../hocs/withAnalytics';
import withRouting, { withRoutingPropType } from '../../../hocs/withRouting';

import { getArray } from '../../../lib/arrays';
import { QUERY_PROPERTY_DETAILS } from './gql';

/**
 * A functional component used to fetch property details.
 *
 * The property is looked up using the slug prop you provide.
 */
const PropertyDetailsContainer = ({ analytics, children, slug, router, dates }) => {
  const { data, loading, error } = useQuery(QUERY_PROPERTY_DETAILS, {
    ssr: false,
    partialRefetch: true,
    errorPolicy: 'all',
    variables: { slug, dates },
  });

  useEffect(() => {
    if (data) {
      const {
        property: { timeZone: propertyTimeZone } = {},
      } = data;

      if (propertyTimeZone) {
        moment.tz.setDefault(propertyTimeZone);
      }
    }
  }, [data]);

  const getAffiliations = () => {
    return getArray(data, 'property.affiliations', []).map(affiliation => ({
      id: affiliation.id.toString(),
      name: affiliation.name,
      src: affiliation.href,
    }));
  };

  const getAmenities = () => {
    return getArray(data, 'property.allAmenities', []).map(amenity => ({
      icon: amenity.iconCode,
      id: amenity.id.toString(),
      title: amenity.name,
    }));
  };

  const getTopAmenities = () => {
    return getArray(data, 'property.topAmenities', []).map(amenity => ({
      icon: amenity.iconCode,
      id: amenity.id.toString(),
      title: amenity.name,
    }));
  };

  const getAllAmenities = () => {
    const amenities = getAmenities();
    const topAmenities = getTopAmenities();
    return [...topAmenities, ...amenities];
  };

  const getDescription = () => {
    let description = get(data, 'property.description');
    if (typeof description === 'string' && description.indexOf('<p>') === -1) {
      description = `<p>${description}</p>`;
    }
    return description;
  };

  const getImages = () => {
    const cardImage = get(data, 'property.cardImage.href');
    const propertyImages = getArray(data, 'property.propertyImages', []).sort((a, b) => a.priorityOrder - b.priorityOrder).map(propertyImage => ({
      desc: propertyImage.description,
      id: propertyImage.id.toString(),
      src: propertyImage.href,
      title: propertyImage.title,
    }));
    return {
      cardImage,
      propertyImages,
    };
  };

  const getSimilarProperties = () => {
    return getArray(data, 'property.similar', []).slice(0, 3).map(property => {
      const amenities = getArray(property, 'topAmenities', []).slice(0, 5).map(amenity => amenity.iconCode);
      const description = get(property, 'shortDescription');
      const location = `${get(property, 'address.city')}, ${get(property, 'address.region')}`;
      const name = get(property, 'name');
      const slug = get(property, 'slug');
      const src = get(property, 'cardImage.href');
      const href = router.getRouteUrl('PropertyDetails', { slug });
      const locationHref = router.getRouteUrl('Map', { location, locationType: 'Address' });
      return {
        alt: name,
        amenities,
        description,
        href,
        id: slug,
        location,
        locationHref,
        name,
        onClick: event => {
          event.preventDefault();
          router.pushRouteAndScroll(href);
          analytics.trackEvent('lodge-click', {
            destinationUrl: href,
            location,
            lodgeName: name,
            sectionName: 'Property Details Similar Listings',
          });
        },
        onLocationClick: event => {
          event.preventDefault();
          router.pushRouteAndScroll(locationHref);
          analytics.trackEvent('location-click', {
            destinationUrl: locationHref,
            location,
            lodgeName: name,
            sectionName: 'Property Details Similar Listings',
          });
        },
        src,
        title: name,
      };
    });
  };

  const buildProperty = () => {
    if (get(data, 'property', null) === null) {
      return null;
    }
    return {
      address: {
        address: get(data, 'property.address.address'),
        city: get(data, 'property.address.city'),
        region: get(data, 'property.address.region'),
        postalCode: get(data, 'property.address.postalCode'),
        country: get(data, 'property.address.country'),
      },
      affiliations: getAffiliations(),
      amenities: getAllAmenities(),
      description: getDescription(),
      id: get(data, 'property.id'),
      images: getImages(),
      location: `${get(data, 'property.address.city')}, ${get(data, 'property.address.region')}`,
      name: get(data, 'property.name'),
      propertyId: get(data, 'property.propertyId'),
      shortDescription: get(data, 'property.shortDescription'),
      similarProperties: getSimilarProperties(),
      slug: get(data, 'property.slug'),
      geo: {
        lat: get(data, 'property.geo.lat'),
        lng: get(data, 'property.geo.lng'),
      },
      timeZone: get(data, 'property.timeZone'),
    };
  };

  if (loading) {
    return <Placeholder />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const renderProps = {
    error,
    loading,
    property: buildProperty(),
  };

  return children(renderProps);
};

PropertyDetailsContainer.propTypes = {
  analytics: withAnalyticsPropType.isRequired,
  children: PropTypes.func.isRequired,
  slug: PropTypes.string.isRequired,
  router: withRoutingPropType.isRequired,
};

export default compose(
  withRouting,
  withAnalytics(),
)(PropertyDetailsContainer);